import React, { useState } from 'react';
import { 
  TruckIcon, 
  WrenchScrewdriverIcon, 
  AcademicCapIcon, 
  ShieldCheckIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface Service {
  id: string;
  title: string;
  description: string;
  features: string[];
  icon: React.ReactNode;
  image: string;
  price: string;
  duration: string;
}

interface ConsultationForm {
  name: string;
  phone: string;
  email: string;
  service: string;
  message: string;
}

const ServicesPage: React.FC = () => {
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [consultationForm, setConsultationForm] = useState<ConsultationForm>({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const services: Service[] = [
    {
      id: 'production',
      title: 'Производство полиэтиленовых труб',
      description: 'Изготовление труб полиэтиленовых для водоснабжения, канализации и газоснабжения. Производственная мощность 6000 тонн в год на 4 экструзионных линиях.',
      features: [
        'Трубы для водоснабжения PN10, PN16',
        'Трубы для канализации SN4, SN8',
        'Трубы для газоснабжения SDR11',
        'Трубы с соэкструзионными слоями',
        'Диаметры от 20 до 1200 мм',
        'Соответствие ГОСТ и международным стандартам'
      ],
      icon: <WrenchScrewdriverIcon className="h-8 w-8" />,
      image: '/images/pipes-delivery-formats.png',
      price: 'от 45 тг/м.п.',
      duration: '1-3 рабочих дня'
    },
    {
      id: 'delivery',
      title: 'Доставка и логистика',
      description: 'Организуем доставку продукции по всему Казахстану и в страны СНГ. Собственный автопарк и партнерская сеть транспортных компаний.',
      features: [
        'Доставка по Уральску - бесплатно',
        'Межгородские перевозки',
        'Международная доставка',
        'Погрузо-разгрузочные работы',
        'Отслеживание груза в режиме реального времени'
      ],
      icon: <TruckIcon className="h-8 w-8" />,
      image: '/images/orange-sewer-pipes.jpg',
      price: 'Расчет индивидуально',
      duration: '1-5 рабочих дней'
    },
    {
      id: 'consultation',
      title: 'Техническое консультирование',
      description: 'Профессиональные консультации по подбору труб, расчету параметров трубопроводов, соблюдению норм и стандартов.',
      features: [
        'Подбор оптимального типа труб',
        'Расчет гидравлических параметров',
        'Консультации по монтажу',
        'Помощь в выборе фитингов',
        'Техническая документация',
        'Сопровождение проектов'
      ],
      icon: <AcademicCapIcon className="h-8 w-8" />,
      image: '/images/gas-pipes-single-layer.png',
      price: 'Бесплатно',
      duration: 'В течение 1 часа'
    },
    {
      id: 'quality',
      title: 'Контроль качества и сертификация',
      description: 'Полный контроль качества продукции на всех этапах производства. Сертификаты соответствия ISO 9001, ISO 14001.',
      features: [
        'Входной контроль сырья',
        'Контроль технологического процесса',
        'Испытания готовой продукции',
        'Сертификаты качества',
        'Протоколы испытаний',
        'Гарантийное обслуживание'
      ],
      icon: <ShieldCheckIcon className="h-8 w-8" />,
      image: '/images/gas-pipes-multi-layer.png',
      price: 'Включено в стоимость',
      duration: 'Постоянно'
    }
  ];

  const handleConsultationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setConsultationForm({
        name: '',
        phone: '',
        email: '',
        service: '',
        message: ''
      });
      
      setTimeout(() => {
        setSubmitSuccess(false);
        setShowConsultationForm(false);
      }, 3000);
    }, 2000);
  };

  const handleInputChange = (field: keyof ConsultationForm, value: string) => {
    setConsultationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-secondary-900">
      {/* Header */}
      <div className="bg-white dark:bg-secondary-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
              🛠️ Наши услуги
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Полный спектр услуг от <span className="text-primary-600">ТОО «KazPolimerPlast»</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-secondary-300 max-w-3xl mx-auto">
              Производство, доставка и техническое сопровождение полиэтиленовых труб 
              для систем водоснабжения, канализации и газоснабжения
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {services.map((service) => (
            <div 
              key={service.id}
              className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden"
            >
              <div className="aspect-w-16 aspect-h-9">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-48 object-cover"
                />
              </div>
              
              <div className="p-8">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mr-4">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                </div>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                
                <div className="space-y-3 mb-6">
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center text-gray-600">
                      <ClockIcon className="h-5 w-5 mr-2" />
                      <span className="text-sm">{service.duration}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-primary-600">{service.price}</span>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => {
                      setConsultationForm(prev => ({ ...prev, service: service.title }));
                      setShowConsultationForm(true);
                    }}
                    className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                  >
                    Заказать услугу
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Why Choose Us Section */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Почему выбирают нас
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Быстрое выполнение</h3>
              <p className="text-gray-600">
                Производственная мощность 6000 тонн в год позволяет выполнять заказы в кратчайшие сроки
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🏆</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Высокое качество</h3>
              <p className="text-gray-600">
                Сертифицированные системы менеджмента качества ISO 9001, экологии ISO 14001
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💡</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Экспертиза</h3>
              <p className="text-gray-600">
                Высококвалифицированный инженерно-технический персонал с многолетним опытом
              </p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-primary-600 rounded-xl text-white p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Готовы обсудить ваш проект?</h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Наши специалисты проконсультируют вас по всем вопросам и помогут выбрать оптимальное решение
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <a 
              href="tel:+77084931267"
              className="flex items-center bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              <PhoneIcon className="h-5 w-5 mr-2" />
              +7 (708) 493-1267
            </a>
            
            <button 
              onClick={() => setShowConsultationForm(true)}
              className="flex items-center bg-primary-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-800 transition-colors"
            >
              <EnvelopeIcon className="h-5 w-5 mr-2" />
              Заказать консультацию
            </button>
          </div>
        </div>
      </div>

      {/* Consultation Form Modal */}
      {showConsultationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Заказать консультацию</h3>
                <button 
                  onClick={() => setShowConsultationForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              {submitSuccess ? (
                <div className="text-center py-8">
                  <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Заявка отправлена!</h4>
                  <p className="text-gray-600">Наш специалист свяжется с вами в ближайшее время</p>
                </div>
              ) : (
                <form onSubmit={handleConsultationSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ваше имя *
                      </label>
                      <input
                        type="text"
                        required
                        value={consultationForm.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Введите ваше имя"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Телефон *
                      </label>
                      <input
                        type="tel"
                        required
                        value={consultationForm.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="+7 (___) ___-__-__"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={consultationForm.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Интересующая услуга
                    </label>
                    <select
                      value={consultationForm.service}
                      onChange={(e) => handleInputChange('service', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Выберите услугу</option>
                      {services.map(service => (
                        <option key={service.id} value={service.title}>{service.title}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Сообщение
                    </label>
                    <textarea
                      value={consultationForm.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Опишите ваши требования или задайте вопрос..."
                    />
                  </div>
                  
                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={() => setShowConsultationForm(false)}
                      className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;